'use client'
import React, { useState } from 'react'

import { ButtonLinkType } from '@/lib/models/components/buttonLink.types'
import { Markdown } from '@/lib/ui-kit/components/Markdown.component'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/lib/ui-kit/components/ui/Accordion.component'
import { ButtonLink } from '@/lib/ui-kit/components/ui/ButtonLink.component'

interface PastEventsAccordion {
  title: string
  description: string
  CTA: ButtonLinkType
}

export interface AccordionPastReportsItem {
  year: string
  pastEventsAccordion: PastEventsAccordion[]
}

interface AccordionPastReportsProps {
  items: AccordionPastReportsItem[]
}

interface AccordionPastReportsItemProps extends AccordionPastReportsItem {
  number: number
  isOpen: boolean
  toggleOpen: (number: number) => void
}

const AccordionPastReportsItems = ({
  number,
  year,
  pastEventsAccordion,
  isOpen,
  toggleOpen,
}: AccordionPastReportsItemProps) => {
  return (
    <AccordionItem value={String(number)}>
      <AccordionTrigger
        className="h-[12rem] px-6 md:h-[10rem] xl:px-10"
        onClick={() => toggleOpen(number)}
      >
        <div className="flex w-full flex-col justify-between md:flex-row md:items-center">
          <div className="flex-1 py-4 text-left text-[1.75rem] lg:text-[2.5rem]">
            {year}
          </div>
          {!isOpen && pastEventsAccordion.length > 0 && (
            <div className="mt-4 flex flex-1 flex-col justify-start text-left text-xl font-semibold md:mt-0 md:flex-row">
              {pastEventsAccordion.map((event, index) => (
                <span key={index} className="md:mr-2">
                  {event.title}
                  {index < pastEventsAccordion.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
          )}
        </div>
      </AccordionTrigger>
      {isOpen && (
        <AccordionContent className="px-6 xl:px-10">
          {' '}
          <div className="flex justify-end pr-16">
            <div className="w-full xl:w-1/2">
              {pastEventsAccordion.map((event, index) => (
                <div key={index} className="mb-16 flex flex-col space-y-4">
                  <h3 className="text-xl font-semibold">{event.title}</h3>
                  <div className="paragraph-l color-[#404040] max-w-5xl pb-4 pt-2">
                    <Markdown>{event.description}</Markdown>
                  </div>
                  <div className="mb-16">
                    <ButtonLink
                      linkUrl={event.CTA.linkUrl}
                      linkType={event.CTA.linkType}
                      buttonType={event.CTA.buttonType}
                    >
                      {event.CTA.buttonLabel}
                    </ButtonLink>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </AccordionContent>
      )}
    </AccordionItem>
  )
}

export const AccordionPastReports: React.FC<AccordionPastReportsProps> = ({
  items,
}) => {
  const [openItem, setOpenItem] = useState<number | null>(null)

  const toggleOpen = (number: number) =>
    setOpenItem(openItem === number ? null : number)

  return (
    <Accordion type="single" collapsible>
      {items.map((item, index) => (
        <AccordionPastReportsItems
          key={index}
          number={index}
          {...item}
          isOpen={openItem === index}
          toggleOpen={toggleOpen}
        />
      ))}
    </Accordion>
  )
}
